import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Loader2, Search } from 'lucide-react'
import { fetchFederalDecisionText, searchFederalCaseLaw } from '../../lib/api/api'
import type { FederalDecisionHit } from '../../lib/api/api'
import SourceTranslation from '../chat/components/SourceTranslation'
import { formatDate } from '../../shared/utils/format'

const COURTS = ['bge', 'bger', 'bstger', 'bvger'] as const
const LANGUAGES = ['it', 'de', 'fr'] as const
type Mode = 'auto' | 'reference' | 'semantic'

/** "Tribunali federali" tab of the case-law page (server /federal-case-law):
 * search by reference or by meaning; the original text with its language, an
 * unofficial translation on request and, for a DTF, the Italian regesto. */
export default function FederalCaseLawTab() {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [mode, setMode] = useState<Mode>('auto')
  const [court, setCourt] = useState('')
  const [language, setLanguage] = useState('')
  const [results, setResults] = useState<FederalDecisionHit[] | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [texts, setTexts] = useState<Record<string, string>>({})
  const [loadingId, setLoadingId] = useState<string | null>(null)

  const runSearch = async (event?: React.FormEvent) => {
    event?.preventDefault()
    const q = query.trim()
    if (!q) return
    setIsSearching(true)
    setError(null)
    try {
      const res = await searchFederalCaseLaw({
        q, mode: mode === 'auto' ? undefined : mode, court: court || undefined, language: language || undefined,
      })
      setResults(res.data)
    } catch (err: any) {
      setResults(null)
      setError(err?.response?.status === 422 ? t('caseLaw.federal.errors.reference') : t('caseLaw.federal.errors.search'))
    } finally {
      setIsSearching(false)
    }
  }

  const toggleText = async (hit: FederalDecisionHit) => {
    if (openId === hit.decision_id) {
      setOpenId(null)
      return
    }
    setOpenId(hit.decision_id)
    if (texts[hit.decision_id]) return
    setLoadingId(hit.decision_id)
    try {
      const res = await fetchFederalDecisionText(hit.decision_id)
      setTexts((prev) => ({ ...prev, [hit.decision_id]: String(res.data) }))
    } catch {
      setError(t('caseLaw.federal.errors.text'))
    } finally {
      setLoadingId(null)
    }
  }

  const selectClass = 'px-2 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800'

  return (
    <div className="space-y-4">
      <form onSubmit={runSearch} className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[16rem] text-sm">
          <span className="block text-gray-600 mb-1">{t('caseLaw.federal.query')}</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('caseLaw.federal.placeholder')}
            className="w-full px-3 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800"
          />
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">{t('caseLaw.federal.modeLabel')}</span>
          <select value={mode} onChange={(e) => setMode(e.target.value as Mode)} className={selectClass}>
            {(['auto', 'reference', 'semantic'] as const).map((m) => (
              <option key={m} value={m}>{t(`caseLaw.federal.mode.${m}`)}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">{t('caseLaw.federal.court')}</span>
          <select value={court} onChange={(e) => setCourt(e.target.value)} className={selectClass}>
            <option value="">{t('caseLaw.federal.allCourts')}</option>
            {COURTS.map((c) => <option key={c} value={c}>{t(`caseLaw.federal.courts.${c}`)}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">{t('caseLaw.federal.language')}</span>
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className={selectClass}>
            <option value="">{t('caseLaw.federal.allLanguages')}</option>
            {LANGUAGES.map((l) => <option key={l} value={l}>{t(`caseLaw.federal.languages.${l}`)}</option>)}
          </select>
        </label>
        <button
          type="submit"
          disabled={isSearching || !query.trim()}
          className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
        >
          {isSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          {t('caseLaw.federal.search')}
        </button>
      </form>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      {results && results.length === 0 && <p className="text-sm text-gray-500">{t('caseLaw.federal.empty')}</p>}

      {results && results.map((hit) => (
        <div key={hit.decision_id} className="p-4 border rounded-lg bg-white dark:bg-gray-800 space-y-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">{hit.citation}</h3>
              <div className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                <span>{hit.court_name}</span>
                {hit.decision_date && <span>{t('caseLaw.list.decisionDate', { date: formatDate(hit.decision_date) })}</span>}
                {hit.language && (
                  <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] font-semibold"
                        title={t('chat.sources.languageTitle')}>
                    {t(`chat.sources.language.${hit.language}`)}
                  </span>
                )}
                {hit.title && <span>{hit.title}</span>}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggleText(hit)}
              className="flex items-center gap-1 px-2 py-1 rounded-md border text-sm text-gray-700 hover:bg-gray-50 flex-shrink-0"
            >
              {loadingId === hit.decision_id ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {openId === hit.decision_id ? t('caseLaw.federal.hideText') : t('caseLaw.federal.showText')}
            </button>
          </div>
          {hit.regesto_it && (
            <div className="text-sm">
              <span className="font-medium text-gray-700">{t('caseLaw.federal.regestoIt')}</span>
              <p className="text-gray-700 dark:text-gray-300">{hit.regesto_it}</p>
            </div>
          )}
          {hit.snippet && (
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
              {hit.snippet}
              {hit.language !== 'it' && <SourceTranslation text={hit.snippet} targetLanguage="it" />}
            </p>
          )}
          {openId === hit.decision_id && texts[hit.decision_id] && (
            <pre className="text-sm whitespace-pre-wrap font-sans p-3 rounded bg-gray-50 dark:bg-gray-900 max-h-[60vh] overflow-y-auto">
              {texts[hit.decision_id]}
            </pre>
          )}
        </div>
      ))}
    </div>
  )
}
